import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getMijnAanmeldingen, getMijnFacturen } from '../services/opleidingenApi';
import { TrainingReviewModal } from '../components/TrainingReviewModal';
import { BookOpen, Calendar, MapPin, Euro, FileText, CheckCircle, Clock, TrendingUp, Star } from 'lucide-react';

export const MijnOpleidingenPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const [reviewModal, setReviewModal] = useState<{ trainingId: number; titel: string } | null>(null);

  const REG_STATUS: Record<string, { label: string; cls: string }> = {
    aangemeld: { label: t('mijnOpleidingen.statusAangemeld'), cls: 'bg-blue-100 text-blue-700' },
    bevestigd: { label: t('mijnOpleidingen.statusBevestigd'), cls: 'bg-green-100 text-green-700' },
    betaald: { label: t('mijnOpleidingen.statusBetaald'), cls: 'bg-emerald-100 text-emerald-700' },
    geannuleerd: { label: t('mijnOpleidingen.statusGeannuleerd'), cls: 'bg-gray-100 text-gray-400' },
  };

  const FACTUUR_STATUS: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
    concept: { label: t('mijnOpleidingen.factuurConcept'), cls: 'bg-gray-100 text-gray-500', icon: <FileText className="w-3.5 h-3.5" /> },
    verzonden: { label: t('mijnOpleidingen.factuurVerzonden'), cls: 'bg-yellow-100 text-yellow-700', icon: <Clock className="w-3.5 h-3.5" /> },
    betaald: { label: t('mijnOpleidingen.factuurBetaald'), cls: 'bg-green-100 text-green-700', icon: <CheckCircle className="w-3.5 h-3.5" /> },
    doorgestort: { label: t('mijnOpleidingen.factuurDoorgestort'), cls: 'bg-emerald-100 text-emerald-700', icon: <TrendingUp className="w-3.5 h-3.5" /> },
  };

  const { data: aanmeldingen = [], isLoading: loadAanm } = useQuery({
    queryKey: ['mijn-aanmeldingen'],
    queryFn: getMijnAanmeldingen,
  });

  const { data: facturen = [], isLoading: loadFact } = useQuery({
    queryKey: ['mijn-facturen'],
    queryFn: getMijnFacturen,
  });

  const openFacturen = facturen.filter((f) => f.status === 'verzonden');

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('mijnOpleidingen.title')}</h1>
            <p className="text-gray-500 mt-1">{t('mijnOpleidingen.subtitle')}</p>
          </div>
          <Link
            to="/opleidingen"
            className="flex items-center gap-2 bg-[#0B1F3A] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1A3459] transition-colors"
          >
            <BookOpen className="w-4 h-4" />
            {t('mijnOpleidingen.browseLink')}
          </Link>
        </div>

        {/* Openstaande facturen banner */}
        {openFacturen.length > 0 && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl flex items-start gap-3">
            <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">
                {t('mijnOpleidingen.openInvoicesBanner', { count: openFacturen.length })}
              </p>
              <p className="text-sm text-yellow-700 mt-0.5">
                {t('mijnOpleidingen.totalDue', { amount: openFacturen.reduce((s, f) => s + Number(f.bedrag_incl_btw), 0).toFixed(2).replace('.', ',') })}
              </p>
              <p className="text-xs text-yellow-600 mt-1">{t('mijnOpleidingen.paymentHint')}</p>
            </div>
          </div>
        )}

        {/* Aanmeldingen */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="p-5 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">{t('mijnOpleidingen.registrationsTitle')}</h2>
          </div>
          {loadAanm ? (
            <div className="p-8 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0B1F3A] mx-auto" /></div>
          ) : aanmeldingen.length === 0 ? (
            <div className="p-10 text-center">
              <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-3">{t('mijnOpleidingen.noRegistrations')}</p>
              <Link to="/opleidingen" className="text-[#0B1F3A] hover:underline text-sm font-medium">
                {t('mijnOpleidingen.browseAvailable')}
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {aanmeldingen.map((reg) => {
                const { label, cls } = REG_STATUS[reg.status] || { label: reg.status, cls: 'bg-gray-100 text-gray-500' };
                return (
                  <div key={reg.id} className="px-5 py-4 flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <Link to={`/opleidingen/${reg.training_id}`} className="font-medium text-gray-900 hover:text-[#0B1F3A]">
                        {reg.training_titel}
                      </Link>
                      <div className="text-xs text-gray-500 mt-0.5">{reg.organisatie_naam}</div>
                      <div className="flex flex-wrap gap-3 mt-2">
                        {reg.wanneer && (
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <Calendar className="w-3 h-3" /> {reg.wanneer}
                          </span>
                        )}
                        {reg.locaties && (
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <MapPin className="w-3 h-3" /> {reg.locaties}
                          </span>
                        )}
                        {reg.prijs_excl_btw != null && (
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <Euro className="w-3 h-3" /> {Number(reg.prijs_excl_btw).toFixed(2).replace('.', ',')} {t('mijnOpleidingen.exclBtw')}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {t('mijnOpleidingen.registeredOn')} {new Date(reg.aangemeld_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
                      {(reg.status === 'bevestigd' || reg.status === 'betaald') && (
                        <button
                          onClick={() => setReviewModal({ trainingId: reg.training_id, titel: reg.training_titel || '' })}
                          className="flex items-center gap-1 text-xs text-[#0B1F3A] hover:underline"
                        >
                          <Star className="w-3 h-3" />
                          {t('mijnOpleidingen.writeReview')}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Facturen */}
        {facturen.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-5 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">{t('mijnOpleidingen.invoicesTitle')}</h2>
            </div>
            {loadFact ? (
              <div className="p-8 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0B1F3A] mx-auto" /></div>
            ) : (
              <div className="divide-y divide-gray-100">
                {facturen.map((f) => {
                  const { label, cls, icon } = FACTUUR_STATUS[f.status] || { label: f.status, cls: 'bg-gray-100 text-gray-500', icon: null };
                  return (
                    <div key={f.id} className="px-5 py-4 flex items-center justify-between gap-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{f.training_titel}</div>
                        <div className="text-xs text-gray-500">{f.organisatie_naam}</div>
                        <div className="text-xs text-gray-400 mt-0.5 font-mono">{f.factuur_nummer}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-gray-900">
                          €{Number(f.bedrag_incl_btw).toFixed(2).replace('.', ',')}
                        </div>
                        <div className="text-xs text-gray-400">{t('mijnOpleidingen.inclBtw')}</div>
                      </div>
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${cls}`}>
                        {icon}{label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="p-4 border-t border-gray-100 bg-gray-50 text-xs text-gray-500 rounded-b-xl">
              {t('mijnOpleidingen.paymentNote')}
            </div>
          </div>
        )}
      </div>

      {reviewModal && (
        <TrainingReviewModal
          trainingId={reviewModal.trainingId}
          trainingTitel={reviewModal.titel}
          onClose={() => setReviewModal(null)}
        />
      )}
    </div>
  );
};

export default MijnOpleidingenPage;
